import { Link } from 'react-router-dom';
import { Footer } from '../components/layout/Footer';

const LAST_UPDATED = 'March 14, 2026';
const CONTACT_EMAIL = 'privacy@vowvisionsai.com';

const sections = [
  {
    id: 'overview',
    title: '1. Overview',
    content: `VowVisionsAI ("we", "us", or "our") operates the VowVisionsAI web application at vowvisionsai.com ("Service"). This Privacy Policy explains how we collect, use, store, and protect your personal information when you use our Service. By using the Service, you agree to the practices described in this policy.

We are committed to protecting your privacy and complying with applicable international data protection laws, including the General Data Protection Regulation (GDPR), the California Consumer Privacy Act (CCPA), the Protection of Personal Information Act (POPIA, South Africa), and applicable provisions of the Apple App Store and Google Play Store guidelines.`
  },
  {
    id: 'data-collected',
    title: '2. What Data We Collect',
    subsections: [
      {
        subtitle: '2.1 Information you provide directly',
        items: [
          'Account information: your name, email address, and password when you create an account.',
          'Invitation content: names, dates, venues, and other event details you enter to generate invitations.',
          'Communication: any messages you send to our support team.',
        ]
      },
      {
        subtitle: '2.2 Information collected automatically',
        items: [
          'Usage data: pages visited, features used, time spent, and interactions within the Service.',
          'Device data: browser type, operating system, screen resolution, and language settings.',
          'Log data: IP address, access times, and referring URLs.',
          'Cookies and similar technologies: session tokens and preference cookies (see Section 8).',
        ]
      },
      {
        subtitle: '2.3 Information we do not collect',
        items: [
          'We do not collect payment card numbers. Payments are processed securely via Stitch Express (when enabled) and we never see or store raw card data.',
          'We do not knowingly collect data from children under 13 (or under 16 in the EEA).',
        ]
      }
    ]
  },
  {
    id: 'how-we-use',
    title: '3. How We Use Your Data',
    items: [
      'To provide and operate the Service, including generating and storing your invitation designs.',
      'To authenticate your account and maintain session security.',
      'To send transactional emails (account confirmation, password reset, export notifications).',
      'To improve and personalise the Service based on aggregated usage analytics.',
      'To respond to support requests and inquiries.',
      'To comply with legal obligations and enforce our Terms of Service.',
      'With your explicit consent, to send promotional updates about new features or offers (you may opt out at any time).',
    ]
  },
  {
    id: 'legal-basis',
    title: '4. Legal Basis for Processing (GDPR)',
    content: `For users in the European Economic Area (EEA) and United Kingdom, we process your personal data under the following legal bases:

• Contract performance – to deliver the Service you signed up for.
• Legitimate interests – to improve and secure the Service, prevent fraud, and conduct analytics.
• Legal obligation – to comply with applicable laws.
• Consent – for marketing communications and non-essential cookies, which you may withdraw at any time.`
  },
  {
    id: 'data-sharing',
    title: '5. Data Sharing & Third Parties',
    content: `We do not sell, rent, or trade your personal data. We may share data with:`,
    items: [
      'Service providers: cloud hosting (Cloudflare), authentication (Supabase), and AI generation (Anthropic API) — each bound by data processing agreements.',
      'Payment processors: Stitch Express processes payments independently under their own privacy policy.',
      'Legal authorities: when required by law, court order, or to protect the rights and safety of users.',
      'Business transfers: in the event of a merger or acquisition, your data may be transferred as part of that transaction with the same privacy protections in place.',
    ]
  },
  {
    id: 'data-retention',
    title: '6. Data Retention',
    content: `We retain your personal data for as long as your account is active or as needed to provide the Service. If you delete your account:

• Your invitation designs are permanently deleted within 30 days.
• Your account credentials are deleted immediately.
• Log data may be retained for up to 12 months for security purposes.
• We may retain anonymised, aggregated analytics data indefinitely.`
  },
  {
    id: 'your-rights',
    title: '7. Your Rights',
    content: `Depending on your location, you have the following rights regarding your personal data:`,
    items: [
      'Access – request a copy of the personal data we hold about you.',
      'Rectification – request correction of inaccurate or incomplete data.',
      'Erasure – request deletion of your data ("right to be forgotten").',
      'Portability – receive your data in a structured, machine-readable format.',
      'Objection – object to processing based on legitimate interests or for direct marketing.',
      'Restriction – request that we restrict processing of your data in certain circumstances.',
      'Withdraw consent – withdraw consent for marketing or non-essential cookies at any time.',
      'Lodge a complaint – with your local supervisory authority (e.g., the ICO in the UK, the Information Regulator in South Africa).',
    ],
    footer: `To exercise any of these rights, please email ${CONTACT_EMAIL}. We will respond within 30 days.`
  },
  {
    id: 'cookies',
    title: '8. Cookies',
    content: `We use the following categories of cookies:

• Strictly necessary: required for authentication and security (cannot be disabled).
• Functional: remember your preferences such as theme or language.
• Analytics: anonymised usage statistics to improve the Service.

You can manage cookie preferences at any time through your browser settings or our cookie banner. We do not use advertising or tracking cookies.`
  },
  {
    id: 'security',
    title: '9. Data Security',
    content: `We implement industry-standard security measures including TLS/HTTPS encryption in transit, encrypted storage at rest, access controls, and regular security reviews. However, no method of transmission over the Internet is 100% secure and we cannot guarantee absolute security.`
  },
  {
    id: 'international',
    title: '10. International Transfers',
    content: `Your data may be processed in countries outside your own. Where we transfer data from the EEA or UK to third countries, we use appropriate safeguards such as Standard Contractual Clauses (SCCs) approved by the European Commission.`
  },
  {
    id: 'children',
    title: "11. Children's Privacy",
    content: `Our Service is not directed at children under 13 (or under 16 in the EEA). We do not knowingly collect personal data from children. If you believe a child has provided us with personal data, please contact us immediately at ${CONTACT_EMAIL} and we will delete it promptly.`
  },
  {
    id: 'changes',
    title: '12. Changes to This Policy',
    content: `We may update this Privacy Policy from time to time. We will notify you of significant changes by email or via a prominent notice within the Service. The "Last updated" date at the top of this policy will always reflect the most recent version. Continued use of the Service after changes constitutes acceptance of the updated policy.`
  },
  {
    id: 'contact',
    title: '13. Contact & Data Controller',
    content: `VowVisionsAI is the data controller for personal data collected through the Service.

For privacy-related questions, requests, or complaints, please contact:
Email: ${CONTACT_EMAIL}
Response time: within 5 business days for general inquiries, within 30 days for formal rights requests.`
  }
];

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#FBF8F3] text-[#211D1A]">
      <section className="border-b border-[#E8E0D6] bg-white pb-16 pt-32 md:pt-36">
        <div className="mx-auto max-w-4xl px-6">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.35em] text-[#8A7A6A]">Legal</p>
          <h1 className="font-serif text-4xl leading-tight text-[#211D1A] md:text-5xl">Privacy Policy</h1>
          <p className="mt-4 text-sm text-[#8A7A6A]">Last updated: {LAST_UPDATED}</p>
          <p className="mt-6 text-base leading-8 text-[#6C6258]">
            Your privacy matters to us. This policy explains clearly and honestly how VowVisionsAI handles your personal data, what rights you have, and how to contact us.
          </p>

          {/* Jump links */}
          <div className="mt-10 flex flex-wrap gap-2">
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="rounded-full border border-[#E4D9CB] bg-white px-4 py-1.5 text-xs font-medium text-[#6C6258] transition hover:border-[#C6A14B] hover:text-[#211D1A]"
              >
                {s.title.replace(/^\d+\.\s/, '')}
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-4xl px-6 space-y-12">
          {sections.map((s) => (
            <div key={s.id} id={s.id} className="scroll-mt-28 rounded-[28px] border border-[#E8DDD1] bg-white p-8">
              <h2 className="font-serif text-2xl text-[#211D1A]">{s.title}</h2>
              {s.content && (
                <p className="mt-4 whitespace-pre-line text-sm leading-8 text-[#5E564F]">{s.content}</p>
              )}
              {s.subsections && s.subsections.map((sub) => (
                <div key={sub.subtitle} className="mt-6">
                  <h3 className="font-medium text-[#211D1A]">{sub.subtitle}</h3>
                  <ul className="mt-3 space-y-2">
                    {sub.items.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm leading-7 text-[#5E564F]">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#C6A14B]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {s.items && (
                <ul className="mt-4 space-y-2">
                  {s.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm leading-7 text-[#5E564F]">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#C6A14B]" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
              {s.footer && (
                <p className="mt-6 rounded-[16px] bg-[#FBF8F3] p-4 text-sm leading-7 text-[#6C6258]">{s.footer}</p>
              )}
            </div>
          ))}

          <div className="rounded-[28px] border border-[#E5D5BF] bg-[linear-gradient(180deg,#FFF9F1,#F8EEDF)] p-8 text-center">
            <p className="font-serif text-xl text-[#211D1A]">Questions about this policy?</p>
            <p className="mt-3 text-sm text-[#6C6258]">Email us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#C6A14B] underline underline-offset-2">{CONTACT_EMAIL}</a> and we'll respond within 5 business days.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3 text-sm">
              <Link to="/terms" className="text-[#6C6258] underline underline-offset-2 hover:text-[#211D1A]">Terms of Service</Link>
              <span className="text-[#C6A14B]">·</span>
              <Link to="/cookies" className="text-[#6C6258] underline underline-offset-2 hover:text-[#211D1A]">Cookie Policy</Link>
              <span className="text-[#C6A14B]">·</span>
              <Link to="/help" className="text-[#6C6258] underline underline-offset-2 hover:text-[#211D1A]">Help Center</Link>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
